import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { ApexDatePicker, toIsoDate } from "../ApexDatePicker";
import {
  Calendar, Check, X, Trash2, RefreshCw, CreditCard, Sparkles, Banknote,
  Download, FileText, MapPin, CarFront, BellRing, Hourglass, AlertTriangle,
  XCircle, MessageSquare, CalendarCheck, Sun, Sunset, CalendarClock, PhoneOff,
} from "lucide-react";

const PART_LABELS = { morning: "Dopoledne", afternoon: "Odpoledne", full: "Celý den" };
const PART_ICONS = { morning: Sun, afternoon: Sunset, full: CalendarClock };

/**
 * BookingRow — Single booking item used in MyBookings (customer view, `mine`)
 * and AllBookings (employee/admin view). Renders status, customer details,
 * rejection panel with proposed term + accept/decline, and an action toolbar
 * that adapts to the viewer role + booking status.
 */

export const STATUS_BADGES = {
  pending: { label: "Čeká na potvrzení", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  confirmed: { label: "Potvrzeno", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  paid: { label: "Zaplaceno", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  completed: { label: "Dokončeno", cls: "bg-purple-500/15 text-purple-300 border-purple-500/30" },
  cancelled: { label: "Zrušeno", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

// 24-hour countdown shown next to pending bookings. Once the countdown expires
// it flips to a red "Přetaženo" badge (booking still stays in the list — admin
// keeps full control). Refreshes every 30s.
function PendingCountdown({ createdAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!createdAt) return null;
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return null;
  const deadline = created + 24 * 60 * 60 * 1000;
  const remaining = deadline - now;
  if (remaining <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-500/15 text-red-300 border border-red-500/40 animate-pulse"
        data-testid="pending-overdue"
      >
        <AlertTriangle className="w-3 h-3" /> Přetaženo · čeká {Math.ceil(Math.abs(remaining) / (60 * 60 * 1000))} h
      </span>
    );
  }
  const totalMin = Math.floor(remaining / (60 * 1000));
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  const urgent = remaining < 3 * 60 * 60 * 1000;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
        urgent ? "bg-amber-500/20 text-amber-200 border-amber-500/50" : "bg-apex-card text-apex-cream/75 border-apex-line"
      }`}
      data-testid="pending-countdown"
    >
      <Hourglass className="w-3 h-3" /> Zbývá {String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")} do 24h
    </span>
  );
}

export default function BookingRow({ b, role, mine, onChange }) {
  const id = b.id || b._id;
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejMsg, setRejMsg] = useState("");
  const [agreeing, setAgreeing] = useState(false);
  const [agreeText, setAgreeText] = useState(b.termAgreedText || "");
  const [agreeDate, setAgreeDate] = useState(b.time ? b.date : "");
  const [agreeTime, setAgreeTime] = useState(b.time || "");
  const badge = STATUS_BADGES[b.status] || { label: b.status, cls: "bg-apex-card text-apex-cream/70 border-apex-line" };
  const updateStatus = async (s) => {
    if (!confirm(`Změnit stav na "${STATUS_BADGES[s]?.label}"?`)) return;
    setBusy(true);
    try { await api.put(`/employee/bookings/${id}/status?new_status=${s}`); onChange?.(); }
    finally { setBusy(false); }
  };
  const submitReject = async () => {
    setBusy(true);
    try {
      await api.post(`/employee/bookings/${id}/reject`, { message: rejMsg || null });
      setRejecting(false);
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Uložení se nezdařilo");
    } finally { setBusy(false); }
  };
  const payNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create-checkout", {
        booking_id: id,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return; // redirecting to Stripe
      }
      alert("Platbu se nepodařilo zahájit.");
      setBusy(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Platbu se nepodařilo zahájit.");
      setBusy(false);
    }
  };
  const markCashPaid = async () => {
    setBusy(true);
    try { await api.put(`/employee/bookings/${id}/mark-cash-paid`); onChange?.(); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!confirm("Opravdu zrušit rezervaci?")) return;
    setBusy(true);
    try { await api.post(`/bookings/${id}/cancel`); onChange?.(); }
    finally { setBusy(false); }
  };
  const sendReminder = async () => {
    if (!confirm("Poslat zákazníkovi e-mailovou připomínku platby?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/employee/bookings/${id}/payment-reminder`, { message: null });
      alert(data?.message || "Připomínka odeslána.");
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Připomínku se nepodařilo odeslat.");
    } finally {
      setBusy(false);
    }
  };
  const acceptProposedTerm = async () => {
    if (!confirm(`Přijmout náhradní termín ${b.proposedDate}${b.proposedTime ? ` v ${b.proposedTime}` : ""}?\n\nRezervace bude obnovena na tento termín.`)) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/accept-proposed-term`);
      alert("Náhradní termín přijat. Brzy vám přijde potvrzovací e-mail.");
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Termín se nepodařilo přijmout.");
    } finally { setBusy(false); }
  };
  const declineProposedTerm = async () => {
    if (!confirm("Opravdu zamítnout navržený náhradní termín?")) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/decline-proposed-term`);
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Akce se nezdařila.");
    } finally { setBusy(false); }
  };
  const downloadIcs = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/bookings/export-calendar/${id}?reminders=1,7`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `apex-detailing-${id}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Kalendář se nepodařilo stáhnout");
    } finally { setBusy(false); }
  };
  const submitAgreeTerm = async () => {
    setBusy(true);
    try {
      await api.post(`/employee/bookings/${id}/agree-term`, { text: agreeText.trim() || null, date: agreeDate, time: agreeTime });
      setAgreeing(false);
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Termín se nepodařilo uložit.");
    } finally { setBusy(false); }
  };
  // Účtenka už není veřejná — vyměníme přihlášení za podepsaný odkaz s expirací.
  const openReceipt = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}/receipt-link`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert(e?.response?.data?.detail || "Účtenku se nepodařilo otevřít.");
    }
  };
  const deleteBooking = async () => {
    if (!confirm("Opravdu natrvalo smazat tuto rezervaci? Tuto akci nelze vrátit zpět.")) return;
    setBusy(true);
    try {
      await api.delete(`/employee/bookings/${id}`);
      onChange?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Rezervaci se nepodařilo smazat.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-apex-cream font-semibold">
            {b.serviceTier?.charAt(0).toUpperCase() + b.serviceTier?.slice(1)} · {b.serviceType}
          </div>
          <div className="text-xs text-apex-cream/60 mt-1">
            {b.time ? `${b.date} · ${b.time} · ` : ""}{b.duration} min · {b.vehicleBrandModel}
          </div>
          {b.customer && (
            <div className="text-xs text-apex-cream/60">
              Zákazník: <span className="text-apex-cream">{b.customer.firstName} {b.customer.lastName}</span> · {b.customer.email} · {b.customer.phone || "—"}
            </div>
          )}
          {!b.termAgreedAt && Array.isArray(b.preferredDays) && b.preferredDays.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`booking-preferred-days-${id}`}>
              {b.preferredDays.map((pd, i) => {
                const Icon = PART_ICONS[pd.part] || Calendar;
                return (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] bg-apex-bg/70 border border-apex-line text-apex-cream/75">
                    <Icon className="w-3 h-3 text-apex-gold" />
                    {new Date(`${pd.date}T00:00:00`).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" })} · {PART_LABELS[pd.part] || pd.part}
                  </span>
                );
              })}
            </div>
          )}
          {b.termAgreedAt && (
            <div className="mt-2.5 rounded-lg border border-apex-gold/40 bg-apex-gold/10 px-3 py-2 text-xs text-apex-cream/90 flex items-start gap-2" data-testid={`booking-agreed-term-${id}`}>
              <CalendarCheck className="w-3.5 h-3.5 text-apex-gold mt-0.5 flex-shrink-0" />
              <div><span className="text-apex-gold font-semibold">Sjednaný termín:</span> {b.date} v {b.time}{b.termAgreedText ? ` — ${b.termAgreedText}` : ""}</div>
            </div>
          )}
          {b.locationType === "mobile" ? (
            <div className="mt-2 inline-flex items-start gap-2 rounded-lg border border-apex-gold/50 bg-apex-gold/10 px-3 py-2 text-xs text-apex-cream" data-testid={`booking-location-${id}`}>
              <MapPin className="w-3.5 h-3.5 text-apex-gold mt-0.5 flex-shrink-0" />
              <span>
                <span className="text-apex-gold font-semibold">Mobilní služba</span> · {b.serviceAddress || "adresa nevyplněna"}
                {b.addressNote && <span className="block text-apex-cream/70">{b.addressNote}</span>}
              </span>
            </div>
          ) : b.locationType === "studio" ? (
            <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-apex-line bg-apex-bg/60 px-3 py-2 text-xs text-apex-cream/75" data-testid={`booking-location-${id}`}>
              <CarFront className="w-3.5 h-3.5 text-apex-gold flex-shrink-0" />
              Zákazník přiveze vůz · Ježkova 627, Zeleneč
            </div>
          ) : null}

          {/* Rejection detail panel — shown for both customer (mine) and staff
              views so everyone has the full context of why the booking was
              cancelled and what alternative term we proposed. */}
          {b.status === "cancelled" && b.rejectionReason === "rejected_by_staff" && (b.employeeNote || b.proposedDate) && (
            <div
              className="mt-3 rounded-xl border border-red-500/30 bg-red-500/5 p-3.5 space-y-2.5"
              data-testid={`booking-rejection-detail-${id}`}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-red-300 font-semibold">
                <XCircle className="w-3.5 h-3.5" />
                Domluva se nezdařila
              </div>
              {b.employeeNote && (
                <div className="flex items-start gap-2 text-sm text-apex-cream/85">
                  <MessageSquare className="w-3.5 h-3.5 text-apex-gold mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-apex-cream/55 mb-0.5">Důvod</div>
                    <div className="whitespace-pre-wrap">{b.employeeNote}</div>
                  </div>
                </div>
              )}
              {b.proposedDate && (
                <div className="flex items-start gap-2 text-sm text-apex-cream/85" data-testid={`booking-proposed-term-${id}`}>
                  <Calendar className="w-3.5 h-3.5 text-apex-gold mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-apex-cream/55 mb-0.5">Navrhujeme náhradní termín</div>
                    <div className="text-apex-gold font-semibold">
                      {b.proposedDate}{b.proposedTime ? ` v ${b.proposedTime}` : ""}
                    </div>
                    {mine && !b.proposedTermResponse && (
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        <button
                          disabled={busy}
                          onClick={acceptProposedTerm}
                          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`accept-proposed-${id}`}
                        >
                          <Check className="w-3 h-3" /> Přijmout termín
                        </button>
                        <button
                          disabled={busy}
                          onClick={declineProposedTerm}
                          className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 inline-flex items-center gap-1 disabled:opacity-50"
                          data-testid={`decline-proposed-${id}`}
                        >
                          <X className="w-3 h-3" /> Zamítnout
                        </button>
                      </div>
                    )}
                    {mine && b.proposedTermResponse === "declined" && (
                      <div className="mt-2 text-[11px] uppercase tracking-wider text-red-300/80">
                        Náhradní termín jste zamítl(a)
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className={`px-3 py-1 rounded-full text-xs border ${badge.cls}`}>{badge.label}</span>
          {b.status === "pending" && <PendingCountdown createdAt={b.createdAt} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-apex-gold font-display text-lg">{Math.round(b.totalPrice).toLocaleString("cs-CZ")} Kč</div>
        <div className="flex flex-wrap gap-2">
          {mine && b.status === "pending" && (
            <button disabled={busy} onClick={cancel} className="text-xs px-3 py-1.5 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`booking-cancel-${id}`}>Zrušit</button>
          )}
          {mine && b.termAgreedAt && b.status === "confirmed" && b.paymentMethod !== "cash" && b.paymentStatus !== "paid" && (
            <button disabled={busy} onClick={payNow} className="text-xs px-4 py-1.5 rounded-lg bg-apex-gold text-apex-bg font-semibold hover:bg-apex-gold-soft transition disabled:opacity-50" data-testid={`booking-pay-${id}`}><CreditCard className="w-3 h-3 inline" /> Zaplatit</button>
          )}
          {(role === "employee" || role === "admin") && b.status === "pending" && (
            <button disabled={busy} onClick={() => setRejecting(v => !v)} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20" data-testid={`booking-reject-${id}`}><PhoneOff className="w-3 h-3 inline" /> Neúspěšná domluva</button>
          )}
          {(role === "employee" || role === "admin") && b.status === "confirmed" && b.paymentMethod !== "cash" && b.paymentStatus !== "paid" && (
            <button
              disabled={busy}
              onClick={sendReminder}
              className="text-xs px-3 py-1.5 rounded-lg bg-apex-gold/15 border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/25 disabled:opacity-50"
              title={[
                b.lastReminderAt ? `Naposledy ručně: ${new Date(b.lastReminderAt).toLocaleString("cs-CZ")}` : null,
                b.autoReminderSentAt ? `Auto-připomínka: ${new Date(b.autoReminderSentAt).toLocaleString("cs-CZ")}` : null,
              ].filter(Boolean).join("\n") || "Poslat zákazníkovi e-mailovou připomínku platby"}
              data-testid={`booking-remind-${id}`}
            >
              <BellRing className="w-3 h-3 inline" /> Připomenout platbu
              {b.autoReminderSentAt && <span className="ml-1 text-[10px] uppercase opacity-80">· auto ✓</span>}
              {b.reminderCount ? <span className="ml-1 text-[10px] opacity-80">({b.reminderCount}×)</span> : null}
            </button>
          )}
          {(role === "employee" || role === "admin") && b.status === "confirmed" && b.paymentMethod === "cash" && b.paymentStatus !== "cash_paid" && (
            <button disabled={busy} onClick={markCashPaid} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"><Banknote className="w-3 h-3 inline" /> Označit jako zaplaceno</button>
          )}
          {(role === "employee" || role === "admin") && (b.status === "confirmed" || b.status === "paid") && (
            <button disabled={busy} onClick={() => updateStatus("completed")} className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/15 border border-purple-500/30 text-purple-300"><Sparkles className="w-3 h-3 inline" /> Dokončit</button>
          )}
          {(role === "employee" || role === "admin") && b.status !== "cancelled" && (
            <button disabled={busy} onClick={() => setAgreeing(v => !v)} className="text-xs px-3 py-1.5 rounded-lg bg-apex-gold/15 border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/25 disabled:opacity-50" data-testid={`booking-agree-term-btn-${id}`}>
              <CalendarCheck className="w-3 h-3 inline" /> Sjednaný termín
            </button>
          )}
          {(b.status === "confirmed" || b.status === "paid" || b.status === "completed") && (
            <>
              <button disabled={busy} onClick={openReceipt} className="text-xs px-3 py-1.5 rounded-lg border border-apex-line text-apex-cream/80 hover:border-apex-gold hover:text-apex-gold" data-testid={`booking-receipt-${id}`}><FileText className="w-3 h-3 inline" /> Účtenka</button>
              <button disabled={busy} onClick={downloadIcs} className="text-xs px-3 py-1.5 rounded-lg border border-apex-line text-apex-cream/80 hover:border-apex-gold hover:text-apex-gold disabled:opacity-50" data-testid={`booking-ical-${id}`}><Download className="w-3 h-3 inline" /> iCal</button>
            </>
          )}
          {role === "admin" && (
            <button disabled={busy} onClick={deleteBooking} className="text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 disabled:opacity-50" data-testid={`booking-delete-${id}`}>
              <Trash2 className="w-3 h-3 inline" /> Smazat
            </button>
          )}
        </div>
      </div>

      {(role === "employee" || role === "admin") && rejecting && (
        <div className="mt-4 rounded-xl bg-apex-bg border border-red-500/25 p-4 space-y-3" data-testid={`reject-form-${id}`}>
          <div className="text-xs text-apex-cream/70 font-semibold uppercase tracking-wider">Oznámit neúspěšnou domluvu</div>
          <textarea
            value={rejMsg}
            onChange={(e) => setRejMsg(e.target.value)}
            rows={2}
            placeholder="Důvod pro zákazníka (volitelné) – např. nezvedal telefon, žádný termín nevyhovoval…"
            className="w-full px-3 py-2 rounded-lg bg-apex-card border border-apex-line text-apex-cream text-sm"
            data-testid={`reject-message-${id}`}
          />
          <div className="flex justify-end gap-2">
            <button disabled={busy} onClick={() => setRejecting(false)} className="text-xs px-3 py-2 rounded-lg border border-apex-line text-apex-cream/70 hover:bg-apex-card">Zrušit</button>
            <button disabled={busy} onClick={submitReject} className="text-xs px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 font-semibold disabled:opacity-50" data-testid={`reject-submit-${id}`}>Oznámit zákazníkovi</button>
          </div>
        </div>
      )}
      {(role === "employee" || role === "admin") && agreeing && (
        <div className="mt-4 rounded-xl bg-apex-bg border border-apex-gold/30 p-4 space-y-3" data-testid={`agree-term-form-${id}`}>
          <div className="text-xs text-apex-cream/70 font-semibold uppercase tracking-wider">Sjednaný termín se zákazníkem</div>
          <label className="block">
            <span className="text-xs text-apex-cream/60 block mb-1">Poznámka (dobrovolné)</span>
            <textarea
              value={agreeText}
              onChange={(e) => setAgreeText(e.target.value)}
              rows={2}
              placeholder="Např. Domluveno telefonicky, zákazník potvrdil termín…"
              className="w-full px-3 py-2 rounded-lg bg-apex-card border border-apex-line text-apex-cream text-sm"
              data-testid={`agree-term-text-${id}`}
            />
          </label>
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-xs text-apex-cream/60">
              <span className="block mb-1">Datum</span>
              <ApexDatePicker
                value={agreeDate ? new Date(`${agreeDate}T00:00:00`) : undefined}
                onSelect={(d) => setAgreeDate(toIsoDate(d))}
                placeholder="Vyberte datum"
                testId={`agree-term-date-${id}`}
              />
            </label>
            <label className="text-xs text-apex-cream/60">
              <span className="block mb-1">Čas</span>
              <input type="time" value={agreeTime} onChange={(e) => setAgreeTime(e.target.value)} className="px-3 py-2 rounded-lg bg-apex-card border border-apex-line text-apex-cream text-sm" data-testid={`agree-term-time-${id}`} />
            </label>
            <div className="flex gap-2 ml-auto">
              <button disabled={busy} onClick={() => setAgreeing(false)} className="text-xs px-3 py-2 rounded-lg border border-apex-line text-apex-cream/70 hover:bg-apex-card">Zrušit</button>
              <button disabled={busy || !agreeDate || !agreeTime} onClick={submitAgreeTerm} className="text-xs px-4 py-2 rounded-lg bg-apex-gold text-apex-bg hover:brightness-110 font-semibold disabled:opacity-50" data-testid={`agree-term-submit-${id}`}>Uložit termín</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
